import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../lib/format';
import { Icon, type IconName } from './icons';

/* ---------------- Rozet ---------------- */
type Tone = 'neutral' | 'signal' | 'ok' | 'warn' | 'danger';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  const className = tone === 'neutral' ? 'badge' : 'badge badge-' + tone;
  return <span className={className}>{children}</span>;
}

/* ---------------- Sayfa başlığı ---------------- */
export function PageHeader({
  title,
  subtitle,
  actions
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line bg-surface px-6 py-3">
      <div className="min-w-0">
        <h1 className="text-[16px] leading-6">{title}</h1>
        {subtitle ? <div className="mt-0.5 text-[12px] text-muted">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/* ---------------- Boş / hata / yükleniyor ---------------- */
export function EmptyState({
  icon = 'inbox',
  title,
  hint,
  action
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon name={icon} size={20} className="text-line-strong" />
      <div className="text-[13px] font-medium text-ink">{title}</div>
      {hint ? <div className="max-w-[380px] text-[12px]">{hint}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="empty" role="alert">
      <Icon name="warning" size={20} className="text-danger" />
      <div className="text-[13px] font-medium text-ink">Veri alınamadı</div>
      <div className="max-w-[420px] text-[12px]">{message}</div>
      {onRetry ? (
        <button type="button" className="btn btn-sm mt-2" onClick={onRetry}>
          <Icon name="refresh" size={14} />
          Tekrar dene
        </button>
      ) : null}
    </div>
  );
}

export function LoadingRows({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="p-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Yükleniyor…</span>
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex gap-3">
            {Array.from({ length: columns }).map((__, columnIndex) => (
              <div
                key={columnIndex}
                className="skeleton h-3"
                style={{ width: columnIndex === 0 ? '28%' : columnIndex === columns - 1 ? '14%' : '20%' }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Form ---------------- */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? <div className="error-text">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  hint,
  id
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  id?: string;
}) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 text-[13px]">
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-[#b4551b]"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <span className="font-medium text-ink">{label}</span>
        {hint ? <span className="mt-0.5 block text-[12px] text-muted">{hint}</span> : null}
      </span>
    </label>
  );
}

/* ---------------- Segmentli filtre ---------------- */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label
}: {
  value: T;
  options: { value: T; label: string; count?: number }[];
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            className={classNames('btn btn-sm', active ? 'border-signal bg-signal-soft text-signal' : 'border-transparent')}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.count !== undefined ? <span className="mono text-[11px] text-muted">{option.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Modal ---------------- */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={wide ? 'modal modal-wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="panel-head">
          <h2 className="text-[14px]">{title}</h2>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Kapat">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="panel-body">{children}</div>
        {footer ? <div className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

/* ---------------- Küçük yardımcılar ---------------- */
export function Stat({
  label,
  value,
  tone,
  title,
  small
}: {
  label: string;
  value: ReactNode;
  tone?: 'ok' | 'warn' | 'danger';
  title?: string;
  small?: boolean;
}) {
  const toneClass = tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : tone === 'danger' ? 'text-danger' : 'text-ink';
  return (
    <div className="panel px-3 py-2">
      <div className="text-[11px] text-muted">{label}</div>
      <div
        title={title}
        className={classNames('mono mt-0.5 truncate font-medium', small ? 'text-[12px]' : 'text-[16px]', toneClass)}
      >
        {value}
      </div>
    </div>
  );
}

export function KeyHint({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((key) => (
        <kbd key={key} className="kbd">
          {key}
        </kbd>
      ))}
    </span>
  );
}

export function ToneDot({ tone }: { tone: 'ok' | 'warn' | 'danger' | 'muted' }) {
  const color = tone === 'ok' ? 'var(--color-ok)' : tone === 'warn' ? 'var(--color-warn)' : tone === 'danger' ? 'var(--color-danger)' : 'var(--color-line-strong)';
  return <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: color }} />;
}
